import { toast } from 'sonner';
import { useOppmoteData } from '@/hooks/useOppmoteData';
import type { OppmoteFormData } from './useOppmoteForm';
import { fjernDekkedeValg } from '@/components/common/TalentSelector';
import type { Oppmote } from '@/services/api/types';

interface UseOppmoteSubmitProps {
  produksjonId: number;
  produksjonTid: string;
  oppmote: Oppmote[];
}

export const useOppmoteSubmit = ({
  produksjonId,
  produksjonTid,
  oppmote,
}: UseOppmoteSubmitProps) => {
  const {
    addOppmote: addOppmoteMutation,
    updateOppmote: updateOppmoteMutation,
    addTalentToOppmote: addTalentToOppmoteMutation,
    removeTalentFromOppmote: removeTalentFromOppmoteMutation,
    refresh,
  } = useOppmoteData(produksjonId);

  const handleSubmit = async (
    formData: OppmoteFormData,
    editingId: number | null,
    setFormError: (error: string | null) => void
  ): Promise<boolean> => {
    setFormError(null);

    if (!formData.navn.trim()) {
      setFormError('Navn er påkrevd');
      return false;
    }

    try {
      const payload: any = {
        navn: formData.navn.trim(),
        beskrivelse: formData.beskrivelse.trim() || undefined,
        rekkefølge: formData.rekkefølge,
      };

      if (formData.tidspunktMode === 'minutterFør') {
        payload.minutterFør = formData.minutterFør;
      } else {
        const prodDate = new Date(produksjonTid);
        const [hours, minutes] = formData.faktiskTid.split(':').map(Number);
        const faktiskTidspunkt = new Date(prodDate);
        faktiskTidspunkt.setHours(hours, minutes, 0, 0);
        payload.tidspunkt = faktiskTidspunkt.toISOString();
      }

      let oppmoteId: number;
      
      if (editingId) {
        await updateOppmoteMutation({
          oppmoteId: editingId,
          data: payload,
        });
        oppmoteId = editingId;
      } else {
        const result = await addOppmoteMutation(payload);
        oppmoteId = result.oppmote.id;
      }

      const talentsToSave: Array<{ navn: string; kategoriSti?: string; erGruppe: boolean }> = [];

      for (const selected of fjernDekkedeValg(formData.selectedTalents)) {
        if (selected.erKategori) {
          talentsToSave.push({
            navn: selected.navn,
            kategoriSti: selected.kategoriSti,
            erGruppe: true,
          });
        } else {
          talentsToSave.push({
            navn: selected.navn,
            kategoriSti: undefined,
            erGruppe: false,
          });
        }
      }

      if (talentsToSave.length > 0) {
        let existingTalents: any[] = [];
        try {
          const existing = oppmote.find(o => o.id === oppmoteId);
          existingTalents = existing?.talenter || [];
        } catch (err) {
          // Ignorer hvis ikke tilgjengelig
        }

        const toRemove = existingTalents.filter(et => {
          const stillSelected = talentsToSave.some(toSave => 
            toSave.navn === et.talent_navn && 
            (toSave.kategoriSti || undefined) === (et.talent_kategori_sti || undefined)
          );
          return !stillSelected;
        });

        for (const removeTalent of toRemove) {
          try {
            await removeTalentFromOppmoteMutation({
              oppmoteId,
              talentNavn: removeTalent.talent_navn,
              talentKategoriSti: removeTalent.talent_kategori_sti || undefined,
            });
          } catch (err) {
            console.error('Feil ved fjerning av talent-kobling:', err);
            toast.error(`Kunne ikke fjerne ${removeTalent.talent_navn} fra oppmøtet`);
          }
        }

        for (const talentToSave of talentsToSave) {
          const alreadyExists = existingTalents.some(et =>
            et.talent_navn === talentToSave.navn &&
            (et.talent_kategori_sti || undefined) === (talentToSave.kategoriSti || undefined)
          );

          if (!alreadyExists) {
            try {
              await addTalentToOppmoteMutation({
                oppmoteId,
                data: {
                  talentNavn: talentToSave.navn,
                  talentKategoriSti: talentToSave.kategoriSti,
                  erGruppe: talentToSave.erGruppe,
                },
              });
            } catch (err) {
              console.error('Feil ved legg til talent-kobling:', err);
              toast.error(`Kunne ikke legge til ${talentToSave.navn} på oppmøtet`);
            }
          }
        }
      } else {
        try {
          const existing = oppmote.find(o => o.id === oppmoteId);
          if (existing?.talenter && existing.talenter.length > 0) {
            for (const talent of existing.talenter) {
              await removeTalentFromOppmoteMutation({
                oppmoteId,
                talentNavn: talent.talent_navn,
                talentKategoriSti: talent.talent_kategori_sti || undefined,
              });
            }
          }
        } catch (err) {
          console.error('Feil ved fjerning av alle talent-koblinger:', err);
          toast.error('Kunne ikke fjerne talenter fra oppmøtet');
        }
      }

      await refresh();
      return true;
    } catch (err: any) {
      console.error('Feil ved lagring av oppmøtetid:', err);
      setFormError(err.response?.data?.error || 'Kunne ikke lagre oppmøtetid');
      return false;
    }
  };

  return { handleSubmit };
};

